// The history facade (#3382 A): the one place a write to a view opens and closes its judge scope.
import type { AgentStream, StreamEntry } from "./types.js";
import type { WriteJudge } from "./write-judge.js";

export type JudgedFields = Record<string, unknown> & { deliberate?: string };

// The label a write is journaled and judged under (#3629).
export type WriterLabel =
  | "hydrate"
  | "prepend"
  | "merge"
  | "ledger"
  | "cards"
  | "live"
  | "outbox"
  | "turn"
  | "verdict"
  | "reset"
  | "clearAll";
// A judgment may name a variant of its lease's writer (a hydrate the guard rejected).
export type JudgedWriter = WriterLabel | "hydrate:rejected";

// Why the facade refused (#3629): the code is the contract, the message only explains it.
export type HistoryInvariantCode =
  | "lease-closed"
  | "foreign-lease"
  | "no-view"
  | "view-after-judgment"
  | "judged-twice"
  | "live-judged"
  | "unjudged"
  | "foreign-view"
  | "write-open"
  | "resetting"
  | "reset-while-writing"
  | "went-async";

export class HistoryInvariantError extends Error {
  constructor(
    readonly code: HistoryInvariantCode,
    message: string,
  ) {
    super(message);
    this.name = "HistoryInvariantError";
  }
}

const refuse = (code: HistoryInvariantCode, message: string): never => {
  throw new HistoryInvariantError(code, message);
};

// What a write returns. `then?: never` makes an async callback, a PromiseLike or a union
// with one a type error; `any` still escapes, so write() checks at runtime too.
export interface Written<T> {
  value: T;
  then?: never;
}

// Lent to one write's callback and revoked when it returns. It carries its conversation and
// its writer, so an inner writer handed a lease needs neither again (#3629).
// judge() ends the scope where the write calls it, exactly once; the lease stays open after.
// The facade reverts nothing: a write that throws leaves what it already changed.
export interface ConversationWrite {
  readonly agentId: string;
  readonly label: WriterLabel;
  readonly stream: AgentStream;
  readonly before: ReadonlySet<number>;
  // An open() write's view, created where the write's own body asks for it.
  ensure(): AgentStream;
  judge(fields?: JudgedFields, writer?: JudgedWriter): void;
  // The one way to assign a view (A-4c): `s` must be this conversation's current stream, and
  // `next` becomes the view's own array; the caller never touches it again.
  assign(s: AgentStream, next: readonly StreamEntry[]): void;
}

// A conversation by its id (an operation opens its own lease) or a lease a caller already
// holds (the operation writes under it).
export type ConversationTarget = string | ConversationWrite;

export interface ConversationHistoryDeps {
  streams: () => ReadonlyMap<string, AgentStream>;
  ensure: (agentId: string) => AgentStream;
  judge: WriteJudge;
  generationOf: (agentId: string) => number;
}

// THE assignment of a conversation's entries; nothing else writes the property.
function writeEntries(s: AgentStream, next: readonly StreamEntry[]): void {
  (s as { entries: readonly StreamEntry[] }).entries = next; // cast: the one writer of a readonly field
}

// Where a write gets its view, relative to its judge scope: an existing one only ("get"),
// created before the scope opens ("ensure"), or created by the body inside it ("inside").
// "lease" has no judge scope at all: a live frame is journaled per run, never judged.
type Acquire = "get" | "ensure" | "inside" | "lease";

const NOTHING_BEFORE: ReadonlySet<number> = new Set();

interface Lease {
  agentId: string;
  label: WriterLabel;
  stream: AgentStream | undefined;
  before: ReadonlySet<number>;
  open: boolean;
  judged: boolean;
  judgedBy: (writer: JudgedWriter, fields: JudgedFields) => void;
  acquire: () => AgentStream;
  assignTo: (s: AgentStream, next: readonly StreamEntry[]) => void;
}

function borrow(lease: Lease): ConversationWrite {
  const { label } = lease;
  const held = <V>(value: V): V => {
    if (!lease.open) refuse("lease-closed", `${label}: the write's lease was used after it closed`);
    return value;
  };
  return {
    // Guarded too: a writer that reads only its conversation still refuses a closed lease.
    get agentId() {
      return held(lease.agentId);
    },
    label,
    get stream() {
      const stream = held(lease.stream);
      return stream ?? refuse("no-view", `${label}: the write has no view yet (call ensure)`);
    },
    get before() {
      return held(lease.before);
    },
    ensure() {
      held(lease);
      if (lease.judged) {
        refuse("view-after-judgment", `${label}: a view was asked for after the judgment`);
      }
      lease.stream = lease.acquire();
      return lease.stream;
    },
    assign(s, next) {
      held(lease);
      lease.assignTo(s, next);
    },
    judge(fields = {}, writer = label) {
      held(lease);
      if (lease.judged) refuse("judged-twice", `${label}: the write was judged twice`);
      // Marked first: a judge that throws part-way is never run again.
      lease.judged = true;
      lease.judgedBy(writer, fields);
    },
  };
}

function isThenable(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    "then" in value &&
    typeof value.then === "function"
  );
}

export class ConversationHistory {
  private readonly writing = new Set<string>();
  // Every lease this facade handed out: what within() trusts a passed lease by.
  private readonly issued = new WeakMap<ConversationWrite, Lease>();
  // A reset of every conversation is running: no write may open until it ends.
  private resetting = false;

  constructor(private readonly deps: ConversationHistoryDeps) {}

  // One synchronous write to an existing view, judged where it calls w.judge. With no stream
  // it returns `missing` without calling fn. A nested write to another conversation is fine.
  write<T>(
    agentId: string,
    label: WriterLabel,
    missing: T,
    fn: (w: ConversationWrite) => Written<T>,
  ): T {
    return this.run(agentId, label, "get", missing, fn);
  }

  // The same, for a write that creates the view it writes before its scope opens (a hydrate).
  create<T>(agentId: string, label: WriterLabel, fn: (w: ConversationWrite) => Written<T>): T {
    const never = undefined as T; // cast: ensure() always returns a stream
    return this.run(agentId, label, "ensure", never, fn);
  }

  // The same, for a write whose scope opens on the view as it is, perhaps none, and whose
  // body creates it with w.ensure() (prepend, the ledger merge).
  open<T>(agentId: string, label: WriterLabel, fn: (w: ConversationWrite) => Written<T>): T {
    const never = undefined as T; // cast: "inside" never returns `missing`
    return this.run(agentId, label, "inside", never, fn);
  }

  // One live frame (or a timer, a continuation): a lease with no judge scope. It never opens,
  // ends or closes one, so it costs nothing per chunk and can't disturb a judged write's.
  lease<T>(agentId: string, label: WriterLabel, fn: (w: ConversationWrite) => Written<T>): T {
    const never = undefined as T; // cast: "lease" never returns `missing`
    return this.run(agentId, label, "lease", never, fn);
  }

  // An operation on a conversation (#3629): given its id it opens a lease; given a lease this
  // facade issued and still holds open, it writes under that one.
  within<T>(target: ConversationTarget, label: WriterLabel, fn: (w: ConversationWrite) => T): T {
    if (typeof target === "string") return this.lease(target, label, (w) => ({ value: fn(w) }));
    const lease = this.issued.get(target);
    if (!lease) refuse("foreign-lease", `${label}: a lease this facade never issued`);
    if (!lease?.open)
      refuse("lease-closed", `${label}: the write's lease was used after it closed`);
    const out = fn(target);
    if (isThenable(out)) refuse("went-async", `${label}: a write to ${target.agentId} went async`);
    return out;
  }

  // Whether a write to agentId is open (a workspace eviction leaves such a conversation be).
  isWriting(agentId: string): boolean {
    return this.writing.has(agentId);
  }

  // A reset of every conversation at once (clearAll): refused before any effect while any
  // write is open, and no write may open while it runs. No judge, no view created.
  resetAll(label: WriterLabel, fn: () => Written<void>): void {
    if (this.resetting || this.writing.size > 0) {
      refuse(
        "reset-while-writing",
        `${label}: a write is open, every conversation can't be reset now`,
      );
    }
    this.resetting = true;
    try {
      if (isThenable(fn())) refuse("went-async", `${label}: a reset went async`);
    } finally {
      this.resetting = false;
    }
  }

  private acquired(agentId: string, acquire: Acquire): AgentStream | undefined {
    return acquire === "ensure" ? this.deps.ensure(agentId) : this.deps.streams().get(agentId);
  }

  private run<T>(
    agentId: string,
    label: WriterLabel,
    acquire: Acquire,
    missing: T,
    fn: (w: ConversationWrite) => Written<T>,
  ): T {
    if (this.resetting) refuse("resetting", `${label}: every conversation is being reset`);
    if (this.writing.has(agentId)) {
      refuse("write-open", `${label}: a write to ${agentId} is already open`);
    }
    this.writing.add(agentId);
    const judged = acquire !== "lease";
    let lease: Lease | undefined;
    try {
      const stream = this.acquired(agentId, acquire);
      if (!stream && acquire === "get") return missing;
      const before = judged ? this.deps.judge.begin(agentId, stream?.entries) : NOTHING_BEFORE;
      const judgedBy = (writer: JudgedWriter, fields: JudgedFields) => {
        if (!judged) refuse("live-judged", `${writer}: a live write is not judged`);
        const entries = this.deps.streams().get(agentId)?.entries ?? [];
        const generation = this.deps.generationOf(agentId);
        this.deps.judge.end(agentId, writer, before, entries, generation, fields);
      };
      const ensure = () => this.deps.ensure(agentId);
      const assignTo = (target: AgentStream, next: readonly StreamEntry[]) => {
        // This lease's conversation's view as it is now: not another's, not one cleared since.
        if (this.deps.streams().get(agentId) !== target) {
          refuse("foreign-view", `${label}: a view of ${agentId} this lease doesn't hold`);
        }
        writeEntries(target, next);
      };
      lease = {
        agentId,
        label,
        stream,
        before,
        open: true,
        judged: false,
        judgedBy,
        acquire: ensure,
        assignTo,
      };
      const w = borrow(lease);
      this.issued.set(w, lease);
      const out = fn(w);
      if (isThenable(out)) refuse("went-async", `${label}: a write to ${agentId} went async`);
      if (judged && !lease.judged) {
        refuse("unjudged", `${label}: a write to ${agentId} returned unjudged`);
      }
      return out.value;
    } finally {
      if (lease) lease.open = false;
      if (judged) this.deps.judge.close(agentId);
      this.writing.delete(agentId);
    }
  }
}
